import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../i18n/LanguageContext';

const LoginContainer = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 20px;
`;

const LoginCard = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  padding: 40px;
  width: 100%;
  max-width: 420px;
`;

const Logo = styled.div`
  text-align: center;
  margin-bottom: 30px;
`;

const LogoText = styled.h1`
  font-size: 28px;
  color: #0c4da2;
  margin: 0;
  font-weight: 700;
`;

const LogoSubtext = styled.p`
  font-size: 14px;
  color: #666;
  margin: 5px 0 0;
`;

const Title = styled.h2`
  font-size: 24px;
  margin-bottom: 30px;
  text-align: center;
  color: #333;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 600;
  color: #333;
`;

const Input = styled.input`
  padding: 12px 16px;
  border: 2px solid #e1e8ed;
  border-radius: 8px;
  font-size: 16px;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: #0c4da2;
    box-shadow: 0 0 0 3px rgba(12, 77, 162, 0.1);
  }
`;

const SubmitButton = styled.button`
  background: linear-gradient(135deg, #0c4da2, #4f94ff);
  color: white;
  padding: 14px;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 10px;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(12, 77, 162, 0.3);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const ErrorMessage = styled.div`
  background: #fee;
  color: #c33;
  padding: 12px;
  border-radius: 6px;
  font-size: 14px;
  text-align: center;
`;

const AdminLoginPage = () => {
  const { t } = useLanguage();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!username || !password) {
      setError(t('pages.adminLogin.errorEmpty'));
      setLoading(false);
      return;
    }

    const result = await login(username, password);
    setLoading(false);

    if (result.success) {
      navigate('/admin/dashboard');
    } else {
      setError(result.message || t('pages.adminLogin.errorInvalid'));
    }
  };

  return (
    <LoginContainer>
      <LoginCard>
        <Logo>
          <LogoText>{t('pages.adminLogin.logo')}</LogoText>
          <LogoSubtext>{t('pages.adminLogin.subLogo')}</LogoSubtext>
        </Logo>
        
        <Title>{t('pages.adminLogin.title')}</Title>
        
        {error && <ErrorMessage>{error}</ErrorMessage>}
        
        <Form onSubmit={handleSubmit}>
          <FormGroup>
            <Label htmlFor="username">{t('pages.adminLogin.username')}</Label>
            <Input
              id="username"
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder={t('pages.adminLogin.usernamePlaceholder')}
              autoComplete="username"
            />
          </FormGroup>
          
          <FormGroup>
            <Label htmlFor="password">{t('pages.adminLogin.password')}</Label>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={t('pages.adminLogin.passwordPlaceholder')}
              autoComplete="current-password"
            />
          </FormGroup>
          
          <SubmitButton type="submit" disabled={loading}>
            {loading ? t('pages.adminLogin.submitLoading') : t('pages.adminLogin.submitIdle')}
          </SubmitButton>
        </Form>

      </LoginCard>
    </LoginContainer>
  );
};

export default AdminLoginPage;
